
import React from 'react';
import { Flame, Heart, Star, Clock, Leaf, Award, ArrowUp, Utensils } from 'lucide-react';
import FaitImage from './FaitImage';
import FadeIn from './FadeIn';
import { useLanguage } from './LanguageContext';
import SEO from './SEO';

const About: React.FC = () => {
  const { t, language } = useLanguage();

  return (
    <div className="w-full overflow-x-hidden">
        <SEO 
          title={language === 'he' ? 'הסיפור שלנו' : 'Our Story'} 
          description={t.about.heroSubtitle}
          path="/story"
        />

        {/* 1. HERO - Story Header */}
        <section className="relative pt-36 pb-32 bg-[#1a120b] border-b border-white/5">
             <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"></div>
             <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
                <FadeIn direction="up">
                  <h1 className="text-5xl md:text-7xl font-serif font-bold text-fait-gold mb-6">
                      {t.about.heroTitle}
                  </h1>
                </FadeIn>
                <FadeIn direction="up" delay={200}>
                  <p className="text-xl md:text-2xl text-gray-300 font-light max-w-2xl mx-auto leading-relaxed">
                      {t.about.heroSubtitle}
                  </p>
                </FadeIn>
             </div>
        </section>

        {/* 2. THE ORIGIN */}
        <section className="py-24 bg-[#140f0b]">
            <div className="max-w-7xl mx-auto px-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                    
                    {/* Image Side */}
                    <div className="relative h-[400px]">
                      <FadeIn direction="right" className="h-full w-full">
                        <div className="absolute top-4 -right-4 rtl:-right-4 rtl:left-auto ltr:-left-4 ltr:right-auto w-full h-full border-2 border-fait-gold/30 rounded-lg"></div>
                        <FaitImage 
                            src="/images/hero-cholent.jpg"
                            alt="Cooking Pot" 
                            className="w-full h-full object-cover rounded-lg shadow-2xl sepia-[0.3] hover:sepia-0 transition-all duration-700 relative z-10"
                            imageContext="cholent"
                        />
                        <div className="absolute -bottom-6 -left-6 rtl:-left-6 rtl:right-auto ltr:-right-6 ltr:left-auto bg-[#251A15] p-6 shadow-xl border border-fait-gold/20 rounded-lg z-20 hidden md:block">
                            <p className="font-serif text-fait-gold text-xl font-bold">{t.about.imageLabelTime}</p>
                            <p className="text-gray-500 text-sm">{t.about.imageLabelText}</p>
                        </div>
                      </FadeIn>
                    </div>

                    {/* Text Side */}
                    <div className="text-right rtl:text-right ltr:text-left">
                        <FadeIn direction="left" delay={200}>
                          <h2 className="text-3xl md:text-4xl font-serif font-bold text-fait-cream mb-6">
                              {t.about.originTitle} <span className="text-fait-gold">{t.about.originYears}</span>
                          </h2>
                          <div className="space-y-6 text-lg text-gray-400 font-sans leading-relaxed">
                              <p>{t.about.originText1}</p>
                              <p>{t.about.originText2}</p>
                              <p>{t.about.originText3}</p>
                              <p className="text-fait-gold font-serif italic text-xl border-r-4 rtl:border-r-4 rtl:border-l-0 ltr:border-l-4 ltr:border-r-0 border-fait-gold pr-4 rtl:pr-4 rtl:pl-0 ltr:pl-4 ltr:pr-0">
                                  {t.about.quote}
                              </p>
                          </div>
                        </FadeIn>
                    </div>
                </div>
            </div>
        </section>

        {/* 3. INGREDIENTS & PROCESS */}
        <section className="py-24 bg-[#1a120b] relative overflow-hidden">
             {/* Background Element */}
             <div className="absolute -right-20 bottom-20 w-96 h-96 bg-fait-gold/5 rounded-full blur-3xl"></div>

             <div className="max-w-6xl mx-auto px-6 relative z-10">
                <div className="text-center mb-16">
                    <FadeIn direction="up">
                      <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-4">{t.about.philTitle}</h2>
                      <p className="text-gray-400 max-w-2xl mx-auto">
                          {t.about.philSubtitle}
                      </p>
                      <div className="w-24 h-1 bg-fait-gold mx-auto mt-6"></div>
                    </FadeIn>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-right rtl:text-right ltr:text-left">
                    {/* Detail 1 */}
                    <FadeIn delay={100} className="h-full">
                      <div className="bg-[#201610] p-8 rounded-2xl border border-white/5 hover:border-fait-gold/30 transition-all duration-300 group hover:-translate-y-2 h-full">
                          <div className="bg-fait-brown/30 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                              <Utensils className="w-8 h-8 text-fait-gold" />
                          </div>
                          <h3 className="text-2xl font-serif font-bold text-fait-cream mb-3">{t.about.details[0].title}</h3>
                          <p className="text-gray-400">
                             {t.about.details[0].text}
                          </p>
                      </div>
                    </FadeIn>

                    {/* Detail 2 */}
                    <FadeIn delay={200} className="h-full">
                      <div className="bg-[#201610] p-8 rounded-2xl border border-white/5 hover:border-fait-gold/30 transition-all duration-300 group hover:-translate-y-2 h-full">
                           <div className="bg-fait-brown/30 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                              <Clock className="w-8 h-8 text-fait-gold" />
                          </div>
                          <h3 className="text-2xl font-serif font-bold text-fait-cream mb-3">{t.about.details[1].title}</h3>
                          <p className="text-gray-400">
                             {t.about.details[1].text}
                          </p>
                      </div>
                    </FadeIn>

                    {/* Detail 3 */}
                    <FadeIn delay={300} className="h-full">
                      <div className="bg-[#201610] p-8 rounded-2xl border border-white/5 hover:border-fait-gold/30 transition-all duration-300 group hover:-translate-y-2 h-full">
                           <div className="bg-fait-brown/30 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                              <Leaf className="w-8 h-8 text-fait-gold" />
                          </div>
                          <h3 className="text-2xl font-serif font-bold text-fait-cream mb-3">{t.about.details[2].title}</h3>
                          <p className="text-gray-400">
                              {t.about.details[2].text}
                          </p>
                      </div>
                    </FadeIn>
                </div>
             </div>
        </section>

        {/* 4. VISUAL BREAK */}
        <section className="py-32 relative">
             <div className="absolute inset-0 z-0">
                 <FaitImage 
                    src="/images/cholent-plate.jpg"
                    alt="Ingredients"
                    className="w-full h-full object-cover"
                    imageContext="meat"
                 />
             </div>
             <div className="absolute inset-0 bg-black/80 z-0"></div>
            <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
                 <FadeIn>
                   <Award className="w-16 h-16 text-fait-gold mx-auto mb-6" />
                   <blockquote className="text-3xl md:text-5xl font-serif font-bold text-white leading-tight mb-8">
                       {t.about.awardQuote}
                   </blockquote>
                   <p className="text-gray-400 font-sans text-xl tracking-widest uppercase">- יהודה פייט -</p>
                 </FadeIn>
            </div>
        </section>

        {/* 5. VALUES & LOCATION */}
        <section className="py-24 bg-[#140f0b]">
            <div className="max-w-5xl mx-auto px-6">
                <FadeIn>
                  <h3 className="text-3xl font-serif font-bold text-white mb-12 text-center">{t.about.valuesTitle}</h3>
                </FadeIn>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-right rtl:text-right ltr:text-left">
                     <FadeIn delay={100} direction="right">
                       <div className="flex gap-6">
                           <Heart className="w-12 h-12 text-red-500 shrink-0 mt-1" />
                           <div>
                               <h3 className="text-2xl font-serif font-bold text-white mb-2">{t.about.values[0].title}</h3>
                               <p className="text-gray-400">{t.about.values[0].text}</p>
                           </div>
                       </div>
                     </FadeIn>
                     <FadeIn delay={200} direction="left">
                       <div className="flex gap-6">
                           <ArrowUp className="w-12 h-12 text-fait-gold shrink-0 mt-1" />
                           <div>
                               <h3 className="text-2xl font-serif font-bold text-white mb-2">{t.about.values[1].title}</h3>
                               <p className="text-gray-400">{t.about.values[1].text}</p>
                           </div>
                       </div>
                     </FadeIn>
                     <FadeIn delay={300} direction="right">
                       <div className="flex gap-6">
                           <Star className="w-12 h-12 text-yellow-500 shrink-0 mt-1" />
                           <div>
                               <h3 className="text-2xl font-serif font-bold text-white mb-2">{t.about.values[2].title}</h3>
                               <p className="text-gray-400">{t.about.values[2].text}</p>
                           </div>
                       </div>
                     </FadeIn>
                     <FadeIn delay={400} direction="left">
                       <div className="flex gap-6">
                           <Flame className="w-12 h-12 text-orange-500 shrink-0 mt-1" />
                           <div>
                               <h3 className="text-2xl font-serif font-bold text-white mb-2">{t.about.values[3].title}</h3>
                               <p className="text-gray-400">{t.about.values[3].text}</p>
                           </div>
                       </div>
                     </FadeIn>
                </div>
            </div>
        </section>

    </div>
  );
};

export default About;
